import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatEUR, formatDate } from "@/lib/formatters";
import { StatusBadge } from "@/components/StatusBadge";
import { Pagination } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateInput } from "@/components/DateInput";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Plus, Search, Receipt, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export default function Proformas() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [skip, setSkip] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.id)));
    }
  };

  const handleExport = async (mode, kind = "pdf") => {
    try {
      setExporting(true);
      const body = mode === "all" ? { all: true } : { ids: Array.from(selected) };
      let path = "/export/proformas", mime = "application/pdf", ext = "pdf";
      if (kind !== "pdf") {
        path = "/export/proformas/sheet";
        body.format = kind;
        ext = kind;
        mime = kind === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      }
      const resp = await api.post(path, body, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `proforma_${mode === "all" ? "tutte" : selected.size}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Esportazione completata");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Export failed:", err);
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api.get("/customers?limit=500").then(r => setCustomers(r.data.items || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (customerFilter !== "all") params.set("customer_id", customerFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("skip", skip);
      params.set("limit", PAGE_SIZE);
      const { data } = await api.get(`/proformas?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Errore nel caricamento fatture proforma");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, customerFilter, dateFrom, dateTo, skip]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setSkip(0); }, [search, statusFilter, customerFilter, dateFrom, dateTo]);

  return (
    <div data-testid="proformas-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tighter font-['Outfit']">Fatture Proforma</h1>
          <p className="text-sm text-zinc-500">{total} fatture totali</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" disabled={exporting} data-testid="export-proformas-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Esporta
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>PDF</DropdownMenuLabel>
              <DropdownMenuItem disabled={selected.size === 0} onClick={() => handleExport("selected", "pdf")} data-testid="export-selected-proformas">
                Selezionate ({selected.size})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("all", "pdf")} data-testid="export-all-proformas">
                Tutte
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Excel (contabilità)</DropdownMenuLabel>
              <DropdownMenuItem disabled={selected.size === 0} onClick={() => handleExport("selected", "xlsx")} data-testid="export-selected-proformas-xlsx">
                Selezionate ({selected.size})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("all", "xlsx")} data-testid="export-all-proformas-xlsx">
                Tutte
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>CSV</DropdownMenuLabel>
              <DropdownMenuItem disabled={selected.size === 0} onClick={() => handleExport("selected", "csv")} data-testid="export-selected-proformas-csv">
                Selezionate ({selected.size})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("all", "csv")} data-testid="export-all-proformas-csv">
                Tutte
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="new-proforma-btn" onClick={() => navigate("/proformas/new")}
            className="bg-[#002FA7] hover:bg-[#002482] rounded-sm">
            <Plus className="w-4 h-4 mr-1" /> Nuova Proforma
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 sm:gap-3">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input data-testid="proforma-search" placeholder="Cerca per numero o oggetto..."
            value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-sm" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="proforma-status-filter" className="w-full sm:w-[150px] rounded-sm">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="DRAFT">Bozza</SelectItem>
            <SelectItem value="ISSUED">Emesso</SelectItem>
            <SelectItem value="SUPERSEDED">Sostituito</SelectItem>
            <SelectItem value="CANCELLED">Annullato</SelectItem>
            <SelectItem value="ARCHIVED">Archiviato</SelectItem>
          </SelectContent>
        </Select>
        {customers.length > 0 && (
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger data-testid="proforma-customer-filter" className="w-full sm:w-[180px] rounded-sm">
              <SelectValue placeholder="Cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-2 w-full sm:w-auto">
          <DateInput data-testid="proforma-date-from" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-sm flex-1 sm:w-[150px]" />
          <DateInput data-testid="proforma-date-to" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-sm flex-1 sm:w-[150px]" />
        </div>
      </div>

      {/* Table (desktop) / Cards (mobile) */}
      <div className="card-surface overflow-hidden">
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="w-10 px-3 py-3">
                  <Checkbox checked={items.length > 0 && selected.size === items.length}
                    onCheckedChange={toggleAll} data-testid="select-all-proformas" />
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Numero</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Oggetto</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden md:table-cell">Cliente</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden sm:table-cell">Data</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Stato</th>
                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Totale</th>
              </tr>
            </thead>
            <tbody>
              {items.map((pf) => (
                <tr key={pf.id}
                  className="border-b border-zinc-100 hover:bg-zinc-50/50 cursor-pointer transition-colors"
                  data-testid={`proforma-row-${pf.id}`}>
                  <td className="w-10 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.has(pf.id)}
                      onCheckedChange={() => toggleSelect(pf.id)} data-testid={`select-proforma-${pf.id}`} />
                  </td>
                  <td className="px-5 py-3" onClick={() => navigate(`/proformas/${pf.id}`)}>
                    <div className="flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-zinc-400" />
                      <span className="text-sm font-mono">{pf.number || "BOZZA"}</span>
                      {pf.proforma_type === "storage_deposit" && (
                        <span className="inline-flex px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm bg-blue-50 text-[#002FA7] border border-[#002FA7]/30">Deposito</span>
                      )}
                      {pf.proforma_type === "storage_release" && (
                        <span className="inline-flex px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm bg-amber-50 text-amber-700 border border-amber-300">Riconsegna</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-zinc-600 max-w-[200px] truncate" onClick={() => navigate(`/proformas/${pf.id}`)}>{pf.subject || "-"}</td>
                  <td className="px-5 py-3 text-sm text-zinc-600 hidden md:table-cell" onClick={() => navigate(`/proformas/${pf.id}`)}>{pf.customer_name || "-"}</td>
                  <td className="px-5 py-3 text-sm text-zinc-600 hidden sm:table-cell" onClick={() => navigate(`/proformas/${pf.id}`)}>{formatDate(pf.date)}</td>
                  <td className="px-5 py-3" onClick={() => navigate(`/proformas/${pf.id}`)}><StatusBadge status={pf.status} /></td>
                  <td className="px-5 py-3 text-sm text-right font-medium" onClick={() => navigate(`/proformas/${pf.id}`)}>{formatEUR(pf.total)}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-sm text-zinc-400">Nessuna fattura proforma trovata</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="sm:hidden divide-y divide-zinc-100">
          {items.map((pf) => (
            <div key={pf.id} className="p-4 active:bg-zinc-50 transition-colors" data-testid={`proforma-card-${pf.id}`}>
              <div className="flex items-start gap-3">
                <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(pf.id)} onCheckedChange={() => toggleSelect(pf.id)} />
                </div>
                <div className="flex-1 min-w-0" onClick={() => navigate(`/proformas/${pf.id}`)}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-mono font-medium truncate flex items-center gap-1.5">
                      {pf.number || "BOZZA"}
                      {pf.proforma_type === "storage_deposit" && (
                        <span className="inline-flex px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider rounded-sm bg-blue-50 text-[#002FA7] border border-[#002FA7]/30 shrink-0">Dep</span>
                      )}
                      {pf.proforma_type === "storage_release" && (
                        <span className="inline-flex px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider rounded-sm bg-amber-50 text-amber-700 border border-amber-300 shrink-0">Ric</span>
                      )}
                    </span>
                    <StatusBadge status={pf.status} />
                  </div>
                  {pf.subject && <p className="text-sm text-zinc-600 truncate mt-1">{pf.subject}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-zinc-400">{pf.customer_name || ""}{pf.customer_name && pf.date ? " · " : ""}{formatDate(pf.date)}</span>
                    <span className="text-sm font-semibold">{formatEUR(pf.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {!loading && items.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-zinc-400">Nessuna fattura proforma trovata</div>
          )}
        </div>
        <Pagination skip={skip} limit={PAGE_SIZE} total={total} onPageChange={setSkip} />
      </div>
    </div>
  );
}
